import type { Stage } from '@api/types'
import { useI18n } from '@i18n/I18nProvider'
import { formatKm } from '@i18n/locale'
import { Icon } from '@ui/Icon'
import { Screen } from '@ui/Screen'
import { ErrorState, LoadingState } from '@ui/States'
import { Link } from 'wouter'
import { stageProgress } from './progress'
import { useMyStages } from './useMyStages'
import { useStages } from './useStages'

// "Mi Camino" del mockup: el avance del caminante arriba y las etapas en orden. Cada etapa es
// una tarjeta que abre su ficha, donde se marca como completada; aquí solo se ve si lo está.
// El registro vive solo en el dispositivo (alcance v1).
export const StagesPage = () => {
	const { t } = useI18n()
	const { data, error, isPending, refetch } = useStages()
	const { mine } = useMyStages()

	if (isPending)
		return (
			<Screen>
				<LoadingState shape="list" />
			</Screen>
		)
	if (error || !data) return <ErrorState error={error} onRetry={() => refetch()} />

	const stages = [...data.items].sort((a, b) => a.order - b.order)
	return (
		<Screen title={t('stages.title')}>
			{stages.length === 0 ? (
				<p className="py-8 text-center text-lg text-earth-800">{t('stages.empty')}</p>
			) : (
				<>
					<MyWay stages={stages} mine={mine} />
					<ol className="mt-6 grid gap-3">
						{stages.map((stage) => (
							<li key={stage.id}>
								<StageCard stage={stage} done={mine[stage.id] !== undefined} />
							</li>
						))}
					</ol>
				</>
			)}
		</Screen>
	)
}

const MyWay = ({ stages, mine }: { stages: Stage[]; mine: Record<string, string> }) => {
	const { t, locale } = useI18n()
	const progress = stageProgress(stages, mine)
	const percent = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)
	return (
		<section aria-labelledby="my-way" className="rounded-3xl bg-sage-200 p-5">
			<h2 id="my-way" className="text-sm font-bold tracking-wide text-olive-900 uppercase">
				{t('stages.myWay')}
			</h2>
			<p className="mt-1 text-2xl font-semibold">
				{t('stages.progress', { done: progress.done, total: progress.total })}
			</p>
			<p className="text-earth-800">
				{t('stages.progressKm', {
					done: formatKm(progress.doneKm, locale),
					total: formatKm(progress.totalKm, locale),
				})}
			</p>
			<div
				role="progressbar"
				aria-label={t('stages.myWay')}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={percent}
				className="mt-3 h-3 overflow-hidden rounded-full bg-white"
			>
				<div className="h-full rounded-full bg-ochre" style={{ width: `${percent}%` }} />
			</div>
			{progress.done === progress.total ? (
				<p className="mt-3 text-sm text-earth-800">{t('stages.allDone')}</p>
			) : (
				progress.done === 0 && <p className="mt-3 text-sm text-earth-800">{t('stages.progressHint')}</p>
			)}
		</section>
	)
}

const StageCard = ({ stage, done }: { stage: Stage; done: boolean }) => {
	const { t, locale } = useI18n()
	return (
		<Link
			href={`/etapas/${encodeURIComponent(stage.id)}`}
			className="flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm active:bg-earth-100"
		>
			<div className="min-w-0 flex-1">
				<p className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-earth-800">
					{t('stages.number', { n: stage.order })} ·{' '}
					{t('stages.distance', { km: formatKm(stage.distanceKm, locale) })}
					{done && (
						<span className="inline-flex items-center gap-1 rounded-full bg-forest-100 px-2 py-0.5 text-forest-800">
							<Icon name="check" className="size-4" />
							{t('stages.done')}
						</span>
					)}
				</p>
				<h3 className="text-xl font-bold text-forest-800">{stage.name}</h3>
				<p className="mt-1 line-clamp-2 text-earth-800">{stage.summary}</p>
			</div>
			<Icon name="forward" className="size-6 shrink-0 text-earth-600" />
		</Link>
	)
}
